import React, { useMemo } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useSupportContext } from '../../context/SupportContext';
import { useAuth } from '../../context/AuthContext';
import {
  Users,
  FileText,
  Gavel,
  CheckSquare,
  TrendingUp,
  Clock,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useAgencyStatistics } from '../../hooks/useAgencyStatistics';

/**
 * Agency Workspace Dashboard — live canonical statistics (no hardcoded samples).
 */
const AgencyWorkspaceDashboard = () => {
  const { agencyId } = useParams();
  const { agency } = useOutletContext() || {};
  const { isInSupportMode } = useSupportContext();
  const { token } = useAuth();

  const { data: stats, loading, error, refresh } = useAgencyStatistics({
    agencyId,
    token,
    mode: 'agency',
    enabled: Boolean(agencyId),
  });

  const agencyName = agency?.name || 'Agency';

  const cards = useMemo(() => {
    const valueOrDash = (v) => (loading ? '…' : (v == null ? '—' : v));
    return [
      {
        label: 'Active Cases',
        value: valueOrDash(stats?.active_cases),
        icon: Users,
        color: 'text-emerald-600',
        bg: 'bg-emerald-100',
      },
      {
        label: 'My Assigned',
        value: valueOrDash(stats?.my_assigned),
        icon: FileText,
        color: 'text-blue-600',
        bg: 'bg-blue-100',
      },
      {
        label: 'Unassigned',
        value: valueOrDash(stats?.unassigned),
        icon: Clock,
        color: 'text-amber-600',
        bg: 'bg-amber-100',
      },
      {
        label: 'Active Disputes',
        value: valueOrDash(stats?.active_disputes),
        icon: Gavel,
        color: 'text-purple-600',
        bg: 'bg-purple-100',
      },
    ];
  }, [stats, loading]);

  const workQueues = [
    { label: 'Document Requests', count: stats?.raw?.queues?.pending_document_requests ?? '—', icon: FileText },
    { label: 'Upload Reviews', count: stats?.raw?.queues?.pending_upload_verifications ?? '—', icon: TrendingUp },
    { label: 'Open Tasks', count: stats?.raw?.queues?.open_tasks ?? '—', icon: CheckSquare },
    { label: 'My Tasks', count: stats?.raw?.queues?.my_tasks ?? '—', icon: CheckSquare },
  ];

  return (
    <div data-testid="agency-workspace-dashboard">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-2xl font-bold text-stone-900">Staff Dashboard</h1>
          {isInSupportMode && (
            <Badge className="bg-amber-100 text-amber-700 border-0">
              Support Mode
            </Badge>
          )}
        </div>
        <p className="text-stone-500">
          {agencyName} overview and work queues
        </p>
      </div>

      {error ? (
        <div
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-center justify-between gap-3"
          data-testid="agency-stats-error"
        >
          <span>{typeof error === 'string' ? error : 'Unable to load agency statistics'}</span>
          <Button type="button" size="sm" variant="outline" onClick={refresh}>
            Retry
          </Button>
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className="bg-white rounded-xl border border-slate-200 p-5"
              data-testid={`agency-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-stone-500">{stat.label}</span>
                <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  {loading ? (
                    <Loader2 size={18} className="animate-spin text-stone-400" />
                  ) : (
                    <Icon size={20} className={stat.color} />
                  )}
                </div>
              </div>
              <p className="text-3xl font-bold text-stone-900">{stat.value}</p>
              <button
                type="button"
                className="flex items-center gap-1 mt-2 text-sm text-stone-500 hover:text-amber-600 transition-colors"
              >
                View all <ArrowRight size={14} />
              </button>
            </div>
          );
        })}
      </div>

      <div className="mb-8">
        <h2 className="text-lg font-semibold text-stone-900 mb-4">Work Queues</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {workQueues.map((queue) => {
            const Icon = queue.icon;
            return (
              <div
                key={queue.label}
                className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Icon size={18} className="text-stone-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">{queue.label}</p>
                  <p className="text-xl font-bold text-stone-900">
                    {loading ? '…' : queue.count}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AgencyWorkspaceDashboard;
